import { useId } from "react";
import type { CuentaCatalogo } from "../types/reviewItem";

/**
 * Selector de cuenta contable con búsqueda, para el catálogo real (~286
 * cuentas en matriz_cuentas_catalogo).
 *
 * Por qué NO infinite scroll / virtualización (preguntado por Liber, ver
 * docs/Decisions.md): esas técnicas resuelven un problema de RENDIMIENTO de
 * renderizado, que acá no existe — 286 <option> son irrelevantes para el
 * navegador. El problema real es de BÚSQUEDA: un <select> nativo solo hace
 * match por primera letra, así que encontrar "6-1-3-002-005 — Gastos de
 * Exportacion" obliga a scrollear a mano.
 *
 * Por qué <datalist> nativo y no un dropdown propio:
 *  - Cero dependencias nuevas (el proyecto no tiene ninguna librería de UI).
 *  - El navegador renderiza el popup FUERA del flujo del DOM. Esto es
 *    determinante: `table.compact td` tiene `overflow: hidden` (global.css),
 *    así que un dropdown propio posicionado en absoluto quedaría RECORTADO
 *    dentro de la celda; evitarlo requeriría un portal de React más lógica
 *    de posicionamiento y scroll.
 *
 * Salvedad conocida: Chromium (Edge/Chrome, los navegadores de uso real acá)
 * filtra por código y por descripción; Firefox filtra solo por el value (el
 * código). Si el filtrado por descripción en Firefox llegara a importar, la
 * alternativa es el dropdown propio con portal.
 *
 * Un <input> no restringe lo que se tipea, así que se VALIDA contra el
 * catálogo: si el valor no es una cuenta existente se marca en rojo y el
 * llamador puede bloquear "Autorizar" con `esCuentaValida`.
 */
interface Props {
  valor: string;
  cuentas: CuentaCatalogo[];
  onChange: (valor: string) => void;
  disabled?: boolean;
}

/** Exportada para que las páginas puedan bloquear la autorización de una fila
 *  con una cuenta inexistente — sin esto se podría mandar a SAP una cuenta
 *  que no está en el catálogo. */
export function esCuentaValida(valor: string, cuentas: CuentaCatalogo[]): boolean {
  return cuentas.some((c) => c.cuenta_contable === valor);
}

export function CuentaCombobox({ valor, cuentas, onChange, disabled }: Props) {
  // useId: puede haber decenas de filas en pantalla, cada input necesita su
  // propio list= (el id tiene que ser único en el documento).
  const listId = useId();
  const valida = valor === "" || esCuentaValida(valor, cuentas);

  return (
    <>
      <input
        type="text"
        list={listId}
        className={valida ? "cuenta-input" : "cuenta-input cuenta-invalida"}
        value={valor}
        disabled={disabled}
        placeholder="Escribi para buscar..."
        title={valida ? valor : `"${valor}" no es una cuenta del catalogo`}
        onChange={(e) => onChange(e.target.value)}
      />
      <datalist id={listId}>
        {cuentas.map((c) => (
          <option key={c.cuenta_contable} value={c.cuenta_contable}>
            {c.descripcion ?? ""}
          </option>
        ))}
      </datalist>
    </>
  );
}
